import React, { useRef } from 'react';
import classnames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import { Bubble } from '@home/components/TeaComponents';
import ChangeLogs, { IChangeLog } from '@gp/components/ChangeLogs';
import NewFeatureTipsContent from '@gp/components/NewFeatureTipsContent';
import useTimeChangeIconBubbleVisible from './_hooks/useTimeChangeIconBubbleVisible';

import type { IBaseProps } from '@home/types/common';
import type { BubbleProps } from 'tea-component';

import './index.scss';

interface ITimeChangeDateProps extends IBaseProps, Omit<BubbleProps, 'visible' | 'children'> {
  rootStyle?: React.CSSProperties;
  rootClassName?: string;
  getViewportRootTimeChangeIcon?: () => HTMLElement | null;
  // 日期
  date?: string;
  // 变更历史
  histories?: IChangeLog[];
  /**
   * 是否强制展示气泡，默认值为undefined，不强制展示
   * true：展示气泡
   * undefined：不展示气泡，并且采用Bubble组件的非受控模式（鼠标悬浮时展示气泡，鼠标离开时不展示气泡），如果值为false，就不能采用Bubble组件的非受控模式了
   */
  forceVisible?: true | undefined;
  // 强制展示气泡的内容
  forceVisibleContent?: BubbleProps['content'];
  // 关闭强制展示气泡的回调
  onCloseForceVisible?: () => void;
}

// change time histories的使用提示文案，用于用户首次接触该功能时，提示用户如何使用该功能
const TIME_CHANGE_HISTORIES_TIPS = 'Hover here to view the history of milestone<br />date changes in the past 30 days.';

const TimeChangeDate: React.FC<ITimeChangeDateProps> = (props) => {
  const {
    rootStyle,
    rootClassName,
    // 默认为pipeline/projectlist页面的表格 作为 intersectionObserver的盒子
    getViewportRootTimeChangeIcon = () => document.querySelector('.tea-table.dbg-projectlist-table > .tea-table__body'),
    // 由于pipeline/projectlist页面的表格的第一列是固定在表格的最左侧，并且第一列的宽度为300px，所以这里通过css的margin属性 声明intersectionObserver盒子的左侧margin为-300px
    getViewportRootMarginTimeChangeIcon = () => '0px 0px 0px -300px',
    style,
    className,
    dark = true,
    placement = 'right',
    flipped = true,
    escapeWithReference = false,
    date,
    histories,
    forceVisible = undefined,
    forceVisibleContent = (
      <NewFeatureTipsContent
        description={TIME_CHANGE_HISTORIES_TIPS}
        onClose={() => {
          onCloseForceVisible?.();
        }}
      />
    ),
    onCloseForceVisible,
  } = props;

  const hasHistories = Array.isArray(histories) && histories.length > 0;
  const bubbleContent = !hasHistories ? undefined : <ChangeLogs data={histories} />;

  const icon = useRef<HTMLDivElement>(null);
  const iconBubbleVisible = useTimeChangeIconBubbleVisible(
    icon,
    forceVisible,
    getViewportRootTimeChangeIcon,
    getViewportRootMarginTimeChangeIcon,
  );

  return (
    <div style={rootStyle} className={classnames('dbg-time-change-date', rootClassName)}>
      {/**
       * 变更记录icon气泡
       * 1、强制展示的情况下，固定展示气泡，气泡内容为forceVisibleContent（跟用户解释这个功能的用途）（仅当用户没有首次手动关闭这个强制展示气泡的时候才会强制展示，用户首次手动关闭强制展示气泡后，就不会强制展示了）
       * 2、非强制展示的情况下，鼠标悬浮展示，鼠标移开就不展示，并且展示内容为bubbleContent
       */}
      {hasHistories && (
        <Bubble
          key={`time-change-icon-${iconBubbleVisible}`}
          style={style}
          overlayStyle={forceVisible ? { zIndex: 1 } : {}}
          className={classnames('dbg-bubble-max-width', className, {
            'dbg-pipeline-project-list-first-time-change-bubble': forceVisible,
          })}
          dark={dark}
          placement={forceVisible ? 'top' : placement}
          flipped={forceVisible ? false : flipped}
          escapeWithReference={forceVisible ? true : escapeWithReference}
          visible={iconBubbleVisible}
          content={forceVisible ? forceVisibleContent : bubbleContent}
        >
          <div ref={icon} className="dbg-time-change-date__time-change-icon">
            <SvgIcon className="dbg-time-change-legend__icon" type="updating" />
          </div>
        </Bubble>
      )}
      {/**
       * 日期的气泡
       * 1、强制展示的情况下，不展示气泡
       * 2、非强制展示的情况下，鼠标悬浮展示气泡，鼠标移开就不展示气泡，气泡内容为bubbleContent
       */}
      <Bubble
        style={style}
        className={classnames('dbg-bubble-max-width', className)}
        dark={dark}
        placement={placement}
        visible={forceVisible ? false : undefined}
        content={forceVisible ? undefined : bubbleContent}
      >
        <div className="dbg-time-change-date__date">
          <span className={classnames({ 'dbg-cursor-pointer': hasHistories })}>{date}</span>
        </div>
      </Bubble>
    </div>
  );
};

export default TimeChangeDate;
